// src/components/EventsDropdown.js
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import '../styles/EventsDropdown.css';

const EVENT_OPTIONS = [
  { label: 'ROM Ceremony Package', to: '/rom-package' },
  { label: 'Corporate Events', to: '/corporate-events' },
  { label: 'Catering Services', to: '/catering' },
  { label: 'Birthday Celebration', to: '/birthday-celebration' },
];

// The hero variant lives inside .hero, which has overflow: hidden (needed to
// contain its background image layer) — an absolutely-positioned dropdown
// menu there gets clipped at the hero's edge no matter how much viewport
// space is actually available below it. Portaling it to <body> with
// JS-computed fixed coordinates sidesteps that clipping entirely.
const PORTAL_VARIANTS = ['hero'];

const EventsDropdown = ({ variant = 'desktop', triggerClassName = '' }) => {
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  const usesPortal = PORTAL_VARIANTS.includes(variant);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Fixed-position portaled menus don't move with the page, so they'd
  // visually detach from the trigger if the user scrolls while open —
  // simplest fix is to just close them.
  useEffect(() => {
    if (!open || !usesPortal) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [open, usesPortal]);

  // Positioned (non-mobile) variants render the menu below the trigger — if
  // there isn't enough room before the bottom of the viewport (e.g. the
  // hero button sitting low on a short mobile screen), flip it to open
  // upward instead so it never gets clipped off-screen.
  useEffect(() => {
    if (!open) {
      if (usesPortal) setMenuStyle(null);
      return;
    }
    if (variant === 'mobile') return;

    const measure = () => {
      const trigger = rootRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;
      const triggerRect = trigger.getBoundingClientRect();
      // visualViewport reflects the space actually visible under a mobile
      // browser's on-screen toolbar; innerHeight can overstate it there.
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const shouldFlip = spaceBelow < menu.offsetHeight + 24;
      setFlip(shouldFlip);

      if (usesPortal) {
        const menuWidth = menu.offsetWidth;
        const centerX = triggerRect.left + triggerRect.width / 2;
        const left = Math.max(8, Math.min(centerX - menuWidth / 2, window.innerWidth - menuWidth - 8));
        setMenuStyle(
          shouldFlip
            ? { left, bottom: viewportHeight - triggerRect.top + 12, top: 'auto' }
            : { left, top: triggerRect.bottom + 12, bottom: 'auto' }
        );
      }
    };

    // Focusing the trigger can make the browser scroll it into view on its
    // own — wait a couple of frames so that settles before measuring.
    let frame2;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(measure);
    });
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, [open, variant, usesPortal]);

  const menu = (
    <ul
      ref={menuRef}
      // Portaled menus (see below) are no longer DOM descendants of the
      // .events-dropdown--{variant} wrapper, so a variant class has to sit
      // directly on the menu itself — a `.events-dropdown--hero .foo`
      // descendant selector would silently never match a portaled `.foo`.
      className={`events-dropdown__menu events-dropdown__menu--${variant}${open ? ' open' : ''}`}
      style={usesPortal ? (menuStyle ?? { visibility: 'hidden' }) : undefined}
    >
      {EVENT_OPTIONS.map(({ label, to }) => (
        <li key={label}>
          <Link to={to} onClick={() => setOpen(false)}>
            {label}
          </Link>
        </li>
      ))}
    </ul>
  );

  return (
    <div
      className={`events-dropdown events-dropdown--${variant}${flip ? ' events-dropdown--flip' : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`events-dropdown__trigger ${triggerClassName}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        Book for Events
        <span className={`events-dropdown__caret${open ? ' open' : ''}`} aria-hidden="true" />
      </button>

      {usesPortal ? createPortal(menu, document.body) : menu}
    </div>
  );
};

export default EventsDropdown;
